import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';

const AsistenciaManagement = () => {
  const { hasPermission, user, loading: authLoading } = useAuth();
  const [socios, setSocios] = useState([]);
  const [asistencias, setAsistencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
  const [filterAño, setFilterAño] = useState(new Date().getFullYear());
  const [guardando, setGuardando] = useState(false);
  const [mesLocked, setMesLocked] = useState(false);

  useEffect(() => {
    fetchSocios();
    fetchAsistencias();
  }, [filterMes, filterAño]);

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  };

  const fetchAsistencias = async () => {
    try {
      const response = await axios.get(`/api/asistencia?mes=${filterMes}&año=${filterAño}`);
      setAsistencias(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asistencias:', error);
    }
  };

  const obtenerAsistenciaSocio = (socioId) => {
    return asistencias.find(a => a.socio._id === socioId);
  };

  const handleToggleAsistencia = async (socioId, asistioActual) => {
    try {
      setGuardando(true);
      const asistencia = obtenerAsistenciaSocio(socioId);
      const nuevoEstado = !asistioActual;

      if (asistencia) {
        // Actualizar
        await axios.put(`/api/asistencia/${asistencia._id}`, {
          asistio: nuevoEstado,
          observaciones: asistencia.observaciones
        });
      } else {
        // Crear
        await axios.post('/api/asistencia', {
          socio: socioId,
          mes: filterMes,
          año: filterAño,
          asistio: nuevoEstado,
          observaciones: ''
        });
      }

      toast.success(nuevoEstado ? 'Asistencia registrada' : 'Asistencia removida');
      fetchAsistencias();
    } catch (error) {
      console.error('Error al guardar asistencia:', error);
      toast.error(error.response?.data?.message || 'Error al guardar asistencia');
    } finally {
      setGuardando(false);
    }
  };

  const calcularEstadisticas = () => {
    return {
      totalSocios: socios.length,
      asistentes: asistencias.filter(a => a.asistio).length,
      ausentes: asistencias.filter(a => !a.asistio).length,
      sinRegistro: socios.length - asistencias.length
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  // Verificar permisos de acceso
  const canAccess = user && ['secretaria', 'admin', 'super-admin'].includes(user.role);
  
  if (!canAccess) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="card-custom p-4">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
              <h3 className="mt-3 text-danger">Acceso Denegado</h3>
              <p className="text-muted">
                No tienes permisos para acceder a esta sección. 
                Se requiere ser Secretaria o Administrador.
              </p>
              <button 
                className="btn btn-primary-custom"
                onClick={() => window.history.back()}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Volver
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  const stats = calcularEstadisticas();

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-clipboard-check me-2"></i>
                      Registro de Asistencia
                    </h2>
                    <p className="text-muted mb-0">
                      Registra la asistencia de los socios a las reuniones mensuales
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Mes
                  </Form.Label>
                  <Form.Select value={filterMes} onChange={(e) => setFilterMes(parseInt(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mes => (
                      <option key={mes} value={mes}>
                        {getNombreMes(mes)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Año
                  </Form.Label>
                  <Form.Select value={filterAño} onChange={(e) => setFilterAño(parseInt(e.target.value))}>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Estadísticas */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stats-card border-info">
              <Card.Body>
                <div className="stats-icon text-info">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stats-number text-info">
                  {stats.totalSocios}
                </div>
                <div className="stats-label">Total de Socios</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-success">
              <Card.Body>
                <div className="stats-icon text-success">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="stats-number text-success">
                  {stats.asistentes}
                </div>
                <div className="stats-label">Asistentes</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-danger">
              <Card.Body>
                <div className="stats-icon text-danger">
                  <i className="fas fa-times-circle"></i>
                </div>
                <div className="stats-number text-danger">
                  {stats.ausentes}
                </div>
                <div className="stats-label">Ausentes</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card border-warning">
              <Card.Body>
                <div className="stats-icon text-warning">
                  <i className="fas fa-question-circle"></i>
                </div>
                <div className="stats-number text-warning">
                  {stats.sinRegistro}
                </div>
                <div className="stats-label">Sin Registrar</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Asistencia */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Asistencia de {getNombreMes(filterMes)} {filterAño}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th>Número Socio</th>
                        <th className="text-center">Asistencia</th>
                        <th className="text-center">Estado</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            No hay socios registrados
                          </td>
                        </tr>
                      ) : (
                        socios.map(socio => {
                          const asistencia = obtenerAsistenciaSocio(socio._id);
                          const asistio = asistencia?.asistio || false;

                          return (
                            <tr key={socio._id} className={asistio ? 'table-success' : asistencia ? 'table-danger' : 'table-light'}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                                  <div>
                                    <div className="fw-bold">{socio.name}</div>
                                    <small className="text-muted">{socio.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>{socio.cedula}</td>
                              <td>
                                <Badge bg="info">{socio.socioInfo?.numeroSocio || 'N/A'}</Badge>
                              </td>
                              <td className="text-center">
                                {asistencia ? (
                                  <div>
                                    {asistio ? (
                                      <Badge bg="success">
                                        <i className="fas fa-check me-1"></i>
                                        Asistió
                                      </Badge>
                                    ) : (
                                      <Badge bg="danger">
                                        <i className="fas fa-times me-1"></i>
                                        No Asistió
                                      </Badge>
                                    )}
                                  </div>
                                ) : (
                                  <Badge bg="secondary">
                                    <i className="fas fa-question me-1"></i>
                                    Sin Registrar
                                  </Badge>
                                )}
                              </td>
                              <td className="text-center">
                                {asistencia ? (
                                  <span className="text-muted small">
                                    {new Date(asistencia.createdAt).toLocaleDateString('es-ES')}
                                  </span>
                                ) : (
                                  <span className="text-muted small">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant={asistio ? 'success' : 'outline-success'}
                                  size="sm"
                                  onClick={() => handleToggleAsistencia(socio._id, asistio)}
                                  disabled={guardando}
                                  className="me-2"
                                  title="Marcar como asistió"
                                >
                                  <i className="fas fa-check me-1"></i>
                                  Asistió
                                </Button>
                                <Button
                                  variant={!asistio && asistencia ? 'danger' : 'outline-danger'}
                                  size="sm"
                                  onClick={() => handleToggleAsistencia(socio._id, asistio)}
                                  disabled={guardando}
                                  title="Marcar como no asistió"
                                >
                                  <i className="fas fa-times me-1"></i>
                                  No Asistió
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Resumen */}
        <Row className="mt-4">
          <Col>
            <Card className="card-custom bg-light">
              <Card.Body>
                <h6 className="mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Resumen de Asistencia
                </h6>
                <Row>
                  <Col md={3}>
                    <p className="mb-2">
                      <strong>Total de Socios:</strong> {stats.totalSocios}
                    </p>
                  </Col>
                  <Col md={3}>
                    <p className="mb-2">
                      <strong className="text-success">Asistentes:</strong> {stats.asistentes} ({stats.totalSocios > 0 ? `${((stats.asistentes / stats.totalSocios) * 100).toFixed(1)}%` : '0%'})
                    </p>
                  </Col>
                  <Col md={3}>
                    <p className="mb-2">
                      <strong className="text-danger">Ausentes:</strong> {stats.ausentes} ({stats.totalSocios > 0 ? `${((stats.ausentes / stats.totalSocios) * 100).toFixed(1)}%` : '0%'})
                    </p>
                  </Col>
                  <Col md={3}>
                    <p className="mb-2">
                      <strong className="text-warning">Sin Registrar:</strong> {stats.sinRegistro}
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Botón de Bloqueo */}
        {hasPermission('asistencia', 'lock') && (
          <Row className="mt-4">
            <Col>
              <Card className="card-custom border-warning">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-2">
                        <i className="fas fa-lock me-2"></i>
                        Bloquear Registro
                      </h6>
                      <p className="text-muted mb-0">
                        Una vez bloqueado, solo el Super Administrador podrá editar este mes
                      </p>
                    </div>
                    <Button
                      variant={mesLocked ? 'danger' : 'warning'}
                      onClick={() => handleLockMonth()}
                      disabled={guardando}
                    >
                      <i className={`fas fa-${mesLocked ? 'unlock' : 'lock'} me-2`}></i>
                      {mesLocked ? 'Desbloquear' : 'Bloquear'} {getNombreMes(filterMes)} {filterAño}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );

  async function handleLockMonth() {
    try {
      setGuardando(true);
      await axios.post('/api/asistencia/lock', {
        mes: filterMes,
        año: filterAño,
        locked: !mesLocked
      });
      toast.success(mesLocked ? 'Mes desbloqueado' : 'Mes bloqueado exitosamente');
      setMesLocked(!mesLocked);
    } catch (error) {
      console.error('Error al bloquear mes:', error);
      toast.error(error.response?.data?.message || 'Error al bloquear el mes');
    } finally {
      setGuardando(false);
    }
  }
};

export default AsistenciaManagement;
